import React, { useState, useEffect, useRef } from 'react';
import { Project } from '../types';
import { uploadProjectPreview } from '../services/storageService';
import { X, Save, RefreshCw, ExternalLink, Monitor, Smartphone, Upload, Loader2 } from 'lucide-react';

interface BatchPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
  onUpdateProject: (p: Project) => Promise<void>;
}

// Sub-component for a single image cell to handle its own loading state and uploads
const PreviewCell = ({ 
    url, 
    projectId,
    type, 
    onUpdate 
}: { 
    url: string | undefined, 
    projectId: string,
    type: 'desktop' | 'mobile', 
    onUpdate: (newUrl: string) => void 
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [currentUrl, setCurrentUrl] = useState(url);
    const [imgKey, setImgKey] = useState(Date.now()); 
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sync with prop only if we aren't loading (prevents jitter)
    useEffect(() => { 
        if (!isLoading) {
            setCurrentUrl(url); 
            setImgKey(Date.now());
        }
    }, [url, isLoading]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsLoading(true);
        try {
            const downloadUrl = await uploadProjectPreview(file, projectId, type);
            setCurrentUrl(downloadUrl);
            onUpdate(downloadUrl);
            setImgKey(Date.now());
        } catch (err: any) {
            alert("Upload failed: " + err.message);
        } finally {
            setIsLoading(false);
            e.target.value = ''; // Reset input
        }
    };

    const triggerUpload = (e: React.MouseEvent) => {
        e.stopPropagation();
        fileInputRef.current?.click();
    };

    return (
        <div 
            className={`relative w-full ${type === 'desktop' ? 'aspect-video' : 'aspect-[9/16] max-w-[150px] mx-auto'} bg-black/20 rounded-lg overflow-hidden border border-white/10 group hover:border-primary/50 transition-all shadow-sm`}
        >
            {/* Hidden File Input */}
            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={handleUpload}
            />

            {/* Image */}
            {currentUrl ? (
                <img 
                    key={imgKey}
                    src={currentUrl} 
                    className={`w-full h-full object-cover object-top transition-all duration-300 ${isLoading ? 'opacity-20 blur-sm scale-95' : 'opacity-100 scale-100'}`}
                    alt={type} 
                    loading="lazy"
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs bg-slate-900/50">
                    <span className="opacity-50">No Preview</span>
                </div>
            )}

            {/* Overlay: Options */}
            <div className={`absolute inset-0 flex flex-row items-center justify-center gap-3 bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${isLoading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                {isLoading ? (
                    <div className="flex flex-col items-center">
                        <Loader2 size={24} className="text-primary animate-spin mb-2" />
                        <span className="text-[10px] font-bold text-white tracking-wider uppercase">Uploading</span>
                    </div>
                ) : (
                    <>
                        <button 
                            onClick={triggerUpload}
                            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors text-white group/btn"
                            title="Manual Upload"
                        >
                            <Upload size={20} className="group-hover/btn:-translate-y-1 transition-transform" />
                            <span className="text-[9px] font-bold uppercase">Upload</span>
                        </button>
                    </>
                )}
            </div>
            
            {/* Badge */}
            <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/70 backdrop-blur-md rounded border border-white/10 text-[9px] text-white font-mono pointer-events-none shadow-lg">
                {type === 'desktop' ? 'Desktop' : 'Mobile'}
            </div>
        </div>
    );
};

export const BatchPreviewModal: React.FC<BatchPreviewModalProps> = ({ isOpen, onClose, projects, onUpdateProject }) => {
  const [localProjects, setLocalProjects] = useState<Project[]>([]);
  const [changedIds, setChangedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });

  // Initialize with sorted projects ONLY when opening
  useEffect(() => {
    if (isOpen) {
        // Sort: Projects missing images -> Newest IDs -> Oldest IDs
        const sorted = [...projects].sort((a, b) => {
            const aMissing = !a.desktopPreviewUrl || !a.mobilePreviewUrl;
            const bMissing = !b.desktopPreviewUrl || !b.mobilePreviewUrl;
            if (aMissing && !bMissing) return -1;
            if (!aMissing && bMissing) return 1;
            return parseInt(b.id) - parseInt(a.id);
        });
        setLocalProjects(sorted);
        setChangedIds(new Set());
        setSaveProgress({ current: 0, total: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); 

  const handleUpdateUrl = (projectId: string, type: 'desktop' | 'mobile', newUrl: string) => {
      setLocalProjects(prev => prev.map(p => {
          if (p.id === projectId) {
              const updated = {
                  ...p,
                  [type === 'desktop' ? 'desktopPreviewUrl' : 'mobilePreviewUrl']: newUrl,
              };
              // If we updated desktop, also update thumbnail fallback
              if (type === 'desktop') {
                  updated.thumbnailUrl = newUrl;
              }
              return updated;
          }
          return p;
      }));
      setChangedIds(prev => new Set(prev).add(projectId));
  };

  const handleSaveAll = async () => {
      if (changedIds.size === 0) return;
      
      setIsSaving(true);
      const toUpdate = localProjects.filter(p => changedIds.has(p.id));
      setSaveProgress({ current: 0, total: toUpdate.length });
      
      try {
          // SEQUENTIAL SAVING
          for (let i = 0; i < toUpdate.length; i++) {
              await onUpdateProject(toUpdate[i]);
              setSaveProgress(prev => ({ ...prev, current: i + 1 }));
              await new Promise(r => setTimeout(r, 50));
          }
          
          onClose();
      } catch (e) {
          alert("Error saving updates to database. Please check console.");
          console.error(e);
      } finally {
          setIsSaving(false);
      }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
      <div className="bg-surface border border-white/10 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col h-[90vh] animate-slide-up ring-1 ring-white/10">
        
        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between bg-surfaceLight/50">
            <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Monitor size={20} className="text-primary" />
                    Preview Audit Manager
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                    Upload images manually to fix missing or incorrect previews.
                </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-colors">
                <X size={24} />
            </button>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-black/40 border-b border-white/5 text-xs font-bold text-slate-400 uppercase tracking-wider sticky top-0 z-10 backdrop-blur-sm">
            <div className="col-span-1 text-center">Day</div>
            <div className="col-span-3">Project Details</div>
            <div className="col-span-4 text-center">Desktop Preview</div>
            <div className="col-span-4 text-center">Mobile Preview</div>
        </div>

        {/* Scrollable List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-black/20 scrollbar-thin">
            {localProjects.map(project => (
                <div key={project.id} className="grid grid-cols-12 gap-6 items-start p-4 rounded-xl bg-surface border border-white/5 hover:border-white/10 transition-colors">
                    
                    {/* Day */}
                    <div className="col-span-1 flex flex-col items-center justify-center h-full pt-4">
                        <span className="text-xl font-mono font-bold text-white/80">#{project.id}</span>
                        {changedIds.has(project.id) && (
                            <span className="mt-2 px-1.5 py-0.5 bg-yellow-500/20 text-yellow-500 text-[9px] rounded uppercase font-bold tracking-wider border border-yellow-500/20">
                                Modified
                            </span>
                        )}
                    </div>

                    {/* Link */}
                    <div className="col-span-3 overflow-hidden pt-2">
                        <h3 className="font-bold text-white truncate text-sm mb-1" title={project.title}>{project.title}</h3>
                        <a 
                            href={project.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-1.5 text-xs text-primary hover:underline truncate mb-2"
                        >
                            <ExternalLink size={10} />
                            {project.url}
                        </a>
                        <div className="flex flex-wrap gap-1">
                            <span className="px-1.5 py-0.5 bg-white/5 rounded text-[10px] text-slate-400 border border-white/5">{project.category}</span>
                        </div>
                    </div>

                    {/* Desktop Image */}
                    <div className="col-span-4">
                        <PreviewCell 
                            url={project.desktopPreviewUrl} 
                            projectId={project.id}
                            type="desktop" 
                            onUpdate={(url) => handleUpdateUrl(project.id, 'desktop', url)}
                        />
                    </div>

                    {/* Mobile Image */}
                    <div className="col-span-4 flex justify-center bg-black/30 rounded-lg p-2 border border-white/5">
                        <PreviewCell 
                            url={project.mobilePreviewUrl} 
                            projectId={project.id}
                            type="mobile" 
                            onUpdate={(url) => handleUpdateUrl(project.id, 'mobile', url)}
                        />
                    </div>

                </div>
            ))}
        </div>

        {/* Footer Actions */}
        <div className="p-5 border-t border-white/10 bg-surfaceLight/50 flex justify-between items-center z-20">
            <div className="text-sm text-slate-400">
                <span className={`font-bold ${changedIds.size > 0 ? 'text-yellow-400' : 'text-white'}`}>{changedIds.size}</span> projects modified
                {isSaving && <span className="ml-2 opacity-75">({saveProgress.current} / {saveProgress.total})</span>}
            </div>
            <div className="flex gap-3">
                <button 
                    onClick={onClose} 
                    disabled={isSaving}
                    className="px-5 py-2.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 transition-colors font-medium text-sm disabled:opacity-50"
                >
                    Cancel
                </button>
                <button 
                    onClick={handleSaveAll} 
                    disabled={isSaving || changedIds.size === 0}
                    className="px-6 py-2.5 rounded-lg bg-primary hover:bg-primaryHover text-white font-bold text-sm shadow-lg shadow-primary/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    {isSaving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>

      </div>
    </div>
  );
};